<template>
  <b-sidebar
    id="edit-item-group-sidebar"
    :visible="!!selectedItemGroupId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @hidden="resetForm"
    @change="(val) => !val && $emit('updated')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">Edit Item Group</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->

      <b-overlay :show="isLoading" spinner-variant="primary">
        <validation-observer ref="refFormObserver">
          <!-- Form -->
          <b-form class="p-2" @submit.prevent="onSubmit" @reset.prevent="resetForm">
            <!-- Parent Group -->
            <validation-provider #default="validationContext" name="Parent Group">
              <b-form-group label="Parent Group" label-for="item-group-parent">
                <v-select
                  v-model="itemGroupData.parentID"
                  :options="parentGroupOptions"
                  :reduce="(option) => option.id"
                  label="name"
                  placeholder="Select parent group (optional)"
                  :clearable="true"
                  :state="getValidationState(validationContext)"
                />

                <!-- Debug: Show current value -->
                <small class="text-muted"
                  >Current parentID value: {{ itemGroupData.parentID }}</small
                >

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Group Code -->
            <validation-provider #default="validationContext" name="Group Code" rules="required">
              <b-form-group label="Group Code" label-for="item-group-code">
                <b-form-input
                  id="item-group-code"
                  v-model="itemGroupData.code"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Enter group code"
                />

                <!-- Debug: Show current value -->
                <small class="text-muted">Current code value: {{ itemGroupData.code }}</small>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Group Name -->
            <validation-provider #default="validationContext" name="Group Name" rules="required">
              <b-form-group label="Group Name" label-for="item-group-name">
                <b-form-input
                  id="item-group-name"
                  v-model="itemGroupData.name"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Enter group name"
                />

                <!-- Debug: Show current value -->
                <small class="text-muted">Current name value: {{ itemGroupData.name }}</small>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Group Type -->
            <validation-provider #default="validationContext" name="Group Type" rules="required">
              <b-form-group label="Group Type" label-for="item-group-type">
                <b-form-select
                  id="item-group-type"
                  v-model="itemGroupData.groupType"
                  :options="groupTypeOptions"
                  :state="getValidationState(validationContext)"
                  @change="(value) => console.log('=== Group Type select changed to:', value)"
                />

                <!-- Debug: Show current value -->
                <small class="text-muted">Current value: {{ itemGroupData.groupType }}</small>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>

              <!-- Debug button to test reactivity -->
              <b-button
                size="sm"
                variant="outline-info"
                class="mt-1"
                @click="
                  () => {
                    itemGroupData.groupType = 'C';
                    console.log('=== Manually set groupType to C:', itemGroupData.value);
                  }
                "
              >
                Test: Set to Control
              </b-button>
            </validation-provider>

            <!-- Item Type - Only shown when Group Type is Control -->
            <validation-provider
              #default="validationContext"
              name="Item Type"
              :rules="itemGroupData.groupType === 'C' ? 'required' : ''"
            >
              <b-form-group
                v-if="itemGroupData.groupType === 'C'"
                label="Item Type"
                label-for="item-group-item-type"
              >
                <b-form-select
                  id="item-group-item-type"
                  v-model="itemGroupData.itemType"
                  :options="itemTypeOptions"
                  :state="getValidationState(validationContext)"
                  placeholder="Select item type"
                />

                <!-- Debug: Show current value -->
                <small class="text-muted"
                  >Current itemType value: {{ itemGroupData.itemType }}</small
                >

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                :disabled="isSubmitting"
              >
                <b-spinner v-if="isSubmitting" small class="mr-1" />
                Update
              </b-button>
              <b-button
                v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                type="button"
                variant="outline-secondary"
                :disabled="isSubmitting"
                @click="hide"
              >
                Cancel
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BFormSelect,
  BFormInvalidFeedback,
  BButton,
  BOverlay,
  BSpinner,
  BCard,
  BCardTitle,
  BCardBody,
} from 'bootstrap-vue';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { ref, computed, watch, onMounted } from '@vue/composition-api';
import { required } from '@validations';
import formValidation from '@core/comp-functions/forms/form-validation';
import Ripple from 'vue-ripple-directive';
import store from '@/store';
import vSelect from 'vue-select';

export default {
  name: 'ItemGroupListEdit',
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BFormSelect,
    BFormInvalidFeedback,
    BButton,
    BOverlay,
    BSpinner,
    BCard,
    BCardTitle,
    BCardBody,
    vSelect,

    // Form Validation
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  props: {
    selectedItemGroupId: {
      type: [Number],
      default: null,
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit, root }) {
    const businessID = computed(() => store.state.user.businessID);
    const isLoading = ref(false);
    const isSubmitting = ref(false);

    const blankItemGroupData = {
      id: 0,
      parentID: null,
      code: '',
      name: '',
      groupType: 'D', // Default to Detail
      itemType: null,
      businessID: null, // Will be set dynamically
    };

    const itemGroupData = ref(JSON.parse(JSON.stringify(blankItemGroupData)));

    const parentGroupOptions = ref([]);
    const groupTypeOptions = [
      { value: 'C', text: 'Control' },
      { value: 'D', text: 'Detail' },
    ];

    const itemTypeOptions = [
      { value: 'Inventory Items', text: 'Inventory Items' },
      { value: 'Non Inventory Items', text: 'Non Inventory Items' },
      { value: 'Services', text: 'Services' },
      { value: 'Fixed Assets', text: 'Fixed Assets' },
    ];

    const resetItemGroupData = () => {
      itemGroupData.value = {
        ...blankItemGroupData,
        businessID: businessID.value,
      };
    };

    const fetchParentGroups = async () => {
      try {
        const response = await store.dispatch('fbr-invoice-item-group/fetchItemGroups', {
          q: '',
          perPage: 1000,
          page: 1,
        });
        parentGroupOptions.value = response.items || [];
      } catch (error) {
        console.error('Error fetching parent groups:', error);
        parentGroupOptions.value = [];
      }
    };

    const fetchItemGroupData = async () => {
      if (!props.selectedItemGroupId) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-item-group/fetchItemGroup', {
          id: props.selectedItemGroupId,
        });

        console.log('=== ItemGroupListEdit fetchItemGroupData response:', response);

        // Handle different possible response structures
        let fetchedItemGroup = null;

        if (response && response.data && response.data.itemGroup) {
          // If response has nested data structure with itemGroup wrapper
          fetchedItemGroup = response.data.itemGroup;
        } else if (response && response.data && response.data.id) {
          // If response.data is the direct item group object
          fetchedItemGroup = response.data;
        } else if (response && response.itemGroup) {
          // If response has itemGroup wrapper at root level
          fetchedItemGroup = response.itemGroup;
        } else if (response && response.id) {
          // If response is the direct item group object
          fetchedItemGroup = response;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        console.log('=== ItemGroupListEdit extracted item group data:', fetchedItemGroup);

        // Populate form with fetched data - map API fields to form fields
        const newFormData = {
          id: fetchedItemGroup.id || 0,
          parentID: fetchedItemGroup.parentID || null,
          code: fetchedItemGroup.code || '',
          name: fetchedItemGroup.name || '',
          groupType: fetchedItemGroup.groupType || 'D',
          itemType: fetchedItemGroup.itemType || null,
          businessID: businessID.value,
        };

        console.log('=== ItemGroupListEdit new form data to set:', newFormData);

        // Force reactivity by creating a new object
        itemGroupData.value = { ...newFormData };

        console.log('=== ItemGroupListEdit populated form data:', itemGroupData.value);
        console.log('=== ItemGroupData ref value after assignment:', itemGroupData.value);
      } catch (error) {
        console.error('Error fetching item group:', error);
        // Reset to blank data on error
        resetItemGroupData();
      } finally {
        isLoading.value = false;
      }
    };

    const onSubmit = async () => {
      // Validate the form first
      const isValid = await refFormObserver.value.validate();
      if (!isValid) return;

      isSubmitting.value = true;

      console.log(
        '=== Current form data before submission:',
        JSON.stringify(itemGroupData.value, null, 2)
      );

      // Prepare data for submission - include the id
      const submissionData = {
        id: props.selectedItemGroupId,
        parentID: itemGroupData.value.parentID,
        code: itemGroupData.value.code,
        name: itemGroupData.value.name,
        groupType: itemGroupData.value.groupType,
        itemType: itemGroupData.value.itemType,
        businessID: businessID.value,
      };

      console.log('=== Submission data:', JSON.stringify(submissionData, null, 2));

      try {
        const response = await store.dispatch(
          'fbr-invoice-item-group/editItemGroup',
          submissionData
        );

        console.log('=== ItemGroupListEdit onSubmit response:', JSON.stringify(response, null, 2));

        if (response.data.status === 'success') {
          root.$bvToast.toast('Item Group updated successfully', {
            title: 'Success',
            variant: 'success',
            solid: true,
          });
        }

        emit('refetch-data');
        emit('updated'); // This will hide the form as parent sets selectedItemGroupId to null
      } catch (error) {
        console.error('Error updating item group:', error);

        // Show user-friendly error message
        let errorMessage = 'Error updating Item Group. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    // Watch for selectedItemGroupId changes to fetch data
    watch(
      () => props.selectedItemGroupId,
      (newId, oldId) => {
        console.log('=== selectedItemGroupId changed from', oldId, 'to', newId);
        if (newId) {
          console.log('=== Fetching data for item group ID:', newId);
          fetchItemGroupData();
        } else {
          console.log('=== Resetting form data');
          resetItemGroupData();
        }
      },
      { immediate: true }
    );

    // Watch for groupType changes to handle itemType visibility
    watch(
      () => itemGroupData.value.groupType,
      (newGroupType, oldGroupType) => {
        console.log('=== Group Type changed from', oldGroupType, 'to', newGroupType);
        if (newGroupType !== 'C') {
          // If group type is not Control, clear item type
          itemGroupData.value.itemType = null;
        }
      }
    );

    // Watch for all form data changes for debugging
    watch(
      itemGroupData,
      (newData, oldData) => {
        console.log('=== Form data changed from:', JSON.stringify(oldData, null, 2));
        console.log('=== Form data changed to:', JSON.stringify(newData, null, 2));
      },
      { deep: true }
    );

    const { refFormObserver, getValidationState, resetForm } = formValidation(resetItemGroupData);

    // Fetch parent groups when component is mounted
    onMounted(() => {
      console.log('=== Component mounted, fetching parent groups');
      fetchParentGroups();
      console.log('=== Initial itemGroupData:', itemGroupData.value);
    });

    return {
      itemGroupData,
      parentGroupOptions,
      groupTypeOptions,
      itemTypeOptions,
      isLoading,
      isSubmitting,
      onSubmit,

      refFormObserver,
      getValidationState,
      resetForm,
    };
  },
};
</script>

<style lang="scss">
#edit-item-group-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }
}
</style>
